import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaChevronUp, FaArrowLeft, FaImages, FaSearch, FaUser, FaHome, FaExpand, FaCamera, FaBars, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import { galleryImages, getOptimalImageSize } from '../../data/HobbiesData/GalleryData/GalleryData';

const GalleryPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (selectedImage) {
      const img = new Image();
      img.src = selectedImage.image;
      img.onload = () => {
        const optimalSize = getOptimalImageSize(img.width, img.height);
        setImageSize(optimalSize);
      };
    }
  }, [selectedImage]);

  const filteredImages = galleryImages.filter(image => 
    image.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    image.camera.toLowerCase().includes(searchQuery.toLowerCase()) ||
    image.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    image.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AnimatePresence>
        {selectedImage && (
          <motion.div 
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div 
              className="relative max-w-2xl max-h-full"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
            >
              <img 
                src={selectedImage.image} 
                alt={selectedImage.title} 
                className="max-w-full max-h-screen object-contain"
              />
              <button 
                className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2"
                onClick={() => setSelectedImage(null)}
              >
                ✕
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-violet-600 text-white pixel-border-bottom relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-repeat" style={{ 
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '30px'
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 py-4 relative z-10">
          {/* Bagian atas header dengan logo dan tombol menu */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <motion.div 
                className="mr-3"
                whileHover={{ rotate: 10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <FaImages className="text-2xl" />
              </motion.div>
              <h1 className="text-2xl md:text-3xl font-bold pixel-font">GALERI FOTO</h1>
            </div>
            
            {/* Tombol menu untuk desktop */}
            <div className="hidden md:flex space-x-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/')}
                className="flex items-center pixel-button bg-violet-700 hover:bg-violet-800 px-3 py-2 text-sm"
                title="Kembali ke Profil"
              >
                <FaHome className="mr-1" />
                <span>Beranda</span>
              </motion.button>
            </div>

            {/* Tombol burger menu untuk mobile */}
            <div className="md:hidden">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleMobileMenu}
                className="pixel-button bg-violet-700 hover:bg-violet-800 p-2"
                aria-label="Toggle Menu"
              >
                {isMobileMenuOpen ? <FaTimes className="text-lg" /> : <FaBars className="text-lg" />}
              </motion.button>
            </div>
          </div>
          
          {/* Menu mobile yang muncul saat tombol burger diklik */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div 
                className="md:hidden bg-violet-700 pixel-border mb-4"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="p-3 space-y-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleNavigation('/')}
                    className="w-full flex items-center justify-center pixel-button bg-violet-600 hover:bg-violet-800 px-3 py-2 text-sm"
                  >
                    <FaHome className="mr-2" />
                    <span>Kembali ke Beranda</span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Bar pencarian */}
          <div className="flex mb-2">
            <div className={`flex items-center pixel-border bg-white px-3 py-2 transition-all duration-300 ${isSearchFocused ? 'ring-2 ring-green-400' : ''} w-full md:w-96`}>
              <FaSearch className="text-gray-400 mr-2" />
              <input
                type="text"
                placeholder="Cari jenis camera, tanggal, kategori atau lokasi."
                className="bg-transparent outline-none text-gray-800 placeholder-gray-400 w-full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
              />
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <p className="text-gray-600 max-w-2xl mx-auto">
            Jelajahi koleksi foto-foto yang saya ambil.
          </p>
        </motion.div>
        
        {searchQuery && (
          <motion.div 
            className="mb-6 p-3 bg-green-50 pixel-border text-green-800 flex items-center justify-between"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div>
              Menampilkan <span className="font-bold">{filteredImages.length}</span> hasil untuk "{searchQuery}"
            </div>
            <button 
              onClick={() => setSearchQuery('')}
              className="text-green-600 hover:text-green-800 font-medium pixel-button bg-green-100 px-2 py-1"
            >
              Bersihkan
            </button>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredImages.map((image, index) => (
              <motion.div 
                key={image.id}
                className="bg-white pixel-border overflow-hidden hover:shadow-lg transition-shadow duration-300"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                layout
              >
                {/* Gambar */}
                <div className="relative group cursor-pointer" onClick={() => setSelectedImage(image)}>
                  <img 
                    src={image.image} 
                    alt={image.title} 
                    className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                    <motion.div 
                      className="text-white opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300"
                      whileHover={{ scale: 1.1 }}
                    >
                      <FaExpand className="text-2xl" />
                    </motion.div>
                  </div>
                </div>

                {/* Konten */}
                <div className="p-4">
                  <div className="grid grid-cols-1 gap-2 text-sm mb-3">
                    <div className="flex items-center text-blue-600 font-medium">
                      <FaCamera className="mr-2" />
                      {image.camera}
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-100 pt-3">
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-600 mr-2">Lokasi:</span>
                        <span className="text-gray-800">{image.location}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-600 mr-2">Tanggal:</span>
                        <span className="text-gray-800">{image.date}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {filteredImages.length === 0 && searchQuery && (
          <motion.div 
            className="text-center py-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <p className="text-gray-600">Tidak ditemukan foto yang sesuai dengan pencarian "{searchQuery}"</p>
          </motion.div>
        )}

        {filteredImages.length === 0 && !searchQuery && (
          <motion.div 
            className="text-center py-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <p className="text-gray-600">Belum ada foto di galeri.</p>
          </motion.div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default GalleryPage;